import React, { useRef, useState, useEffect } from "react";
import { Camera, Upload, RotateCcw, Check, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Captures/uploads a photo and returns stored path via onUploaded(path)
export default function PhotoCapture({ category = "uploads", onUploaded, label = "Ambil Foto", testIdPrefix = "photo", facingMode = "user", multiple = false }) {
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [preview, setPreview] = useState(null); // dataURL
  const [blob, setBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [facing, setFacing] = useState(facingMode);
  const streamRef = useRef(null);

  useEffect(() => () => stopCam(), []);

  // Attach the stream only after the <video> element is mounted (fixes black screen)
  useEffect(() => {
    if (streaming && streamRef.current && videoRef.current) {
      const v = videoRef.current;
      v.srcObject = streamRef.current;
      v.play?.().catch(() => {});
    }
  }, [streaming]);

  const openStream = async (mode) => {
    stopCam();
    // Try requested camera, fall back to any available camera
    try {
      return await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: mode } }, audio: false });
    } catch (e) {
      return await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
    }
  };

  const startCam = async () => {
    try {
      const stream = await openStream(facing);
      streamRef.current = stream;
      setStreaming(true);
    } catch (e) {
      toast.error("Kamera tidak tersedia. Silakan unggah foto dari galeri.");
      fileRef.current?.click();
    }
  };

  const flipCam = async () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    try {
      const stream = await openStream(next);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play?.().catch(() => {});
      }
    } catch (e) {
      toast.error("Tidak bisa mengganti kamera di perangkat ini");
    }
  };

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStreaming(false);
  };

  const snap = () => {
    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((b) => {
      setBlob(b);
      setPreview(canvas.toDataURL("image/jpeg"));
      stopCam();
    }, "image/jpeg", 0.9);
  };

  const uploadBlob = async (b, name) => {
    const form = new FormData();
    const ext = b.type === "image/png" ? "png" : (b.type === "image/webp" ? "webp" : "jpg");
    form.append("file", b, name || `foto.${ext}`);
    form.append("category", category);
    const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (multiple) {
      setUploading(true);
      try {
        for (const f of files) {
          const path = await uploadBlob(f, f.name);
          onUploaded?.(path);
        }
        toast.success(`${files.length} foto diunggah`);
      } catch (err) {
        toast.error("Gagal mengunggah sebagian foto");
      } finally {
        setUploading(false);
        e.target.value = "";
      }
      return;
    }
    const f = files[0];
    setBlob(f);
    setPreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setPreview(null);
    setBlob(null);
  };

  const doUpload = async () => {
    if (!blob) return;
    setUploading(true);
    try {
      const path = await uploadBlob(blob);
      toast.success("Foto berhasil diunggah");
      onUploaded?.(path, preview);
      if (multiple) reset();
    } catch (e) {
      toast.error("Gagal mengunggah foto");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple={multiple} className="hidden" onChange={onFile} data-testid={`${testIdPrefix}-file-input`} />

      {!preview && !streaming && (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button type="button" onClick={startCam} className="rounded-xl h-12 bg-teal-600 hover:bg-teal-700" data-testid={`${testIdPrefix}-open-camera-btn`}>
            <Camera className="w-5 h-5 mr-2" /> {label}
          </Button>
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-xl h-12" data-testid={`${testIdPrefix}-upload-btn`}>
            <Upload className="w-5 h-5 mr-2" /> Unggah dari Galeri
          </Button>
        </div>
      )}

      {streaming && (
        <div className="space-y-3">
          <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-md rounded-2xl border-4 border-teal-200 bg-black" />
          <div className="flex gap-3 flex-wrap">
            <Button type="button" onClick={snap} className="rounded-xl bg-amber-500 hover:bg-amber-600" data-testid={`${testIdPrefix}-snap-btn`}>
              <Camera className="w-5 h-5 mr-2" /> Jepret
            </Button>
            <Button type="button" variant="outline" onClick={flipCam} className="rounded-xl" data-testid={`${testIdPrefix}-flip-btn`}>
              <RefreshCw className="w-5 h-5 mr-2" /> {facing === "user" ? "Kamera Belakang" : "Kamera Depan"}
            </Button>
            <Button type="button" variant="outline" onClick={stopCam} className="rounded-xl">Batal</Button>
          </div>
        </div>
      )}

      {preview && (
        <div className="space-y-3">
          <img src={preview} alt="preview" className="w-full max-w-md rounded-2xl border-4 border-teal-200 object-cover" data-testid={`${testIdPrefix}-preview`} />
          <div className="flex gap-3">
            <Button type="button" onClick={doUpload} disabled={uploading} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid={`${testIdPrefix}-confirm-btn`}>
              {uploading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />} Gunakan Foto Ini
            </Button>
            <Button type="button" variant="outline" onClick={reset} className="rounded-xl" data-testid={`${testIdPrefix}-retake-btn`}>
              <RotateCcw className="w-5 h-5 mr-2" /> Ulangi
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
